import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useStore } from "@/lib/useStore";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StorefrontLayout from "@/components/layouts/StorefrontLayout";
import { NotFound } from "@/pages/ErrorPages";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

export default function StorefrontSignup() {
  const { slug, sp } = useStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signup } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [tenantStatus, setTenantStatus] = useState("loading");
  const [form, setForm] = useState({
    full_name: "",
    email: searchParams.get("email") || "",
    password: "",
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const intent = searchParams.get("intent") || "";
  const targetContent = searchParams.get("content") || "";

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/public/tenants/${slug}`)
      .then((r) => {
        if (!cancelled) {
          setTenant(r.data);
          setTenantStatus("ready");
        }
      })
      .catch(() => !cancelled && setTenantStatus("not_found"));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (tenantStatus === "loading") return null;
  if (tenantStatus === "not_found" || !tenant) return <NotFound />;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await signup({ ...form, tenant_slug: slug });
      const dest = intent === "watch" && targetContent
        ? sp(`/select-profile?next=${encodeURIComponent(sp(`/title/${targetContent}`))}`)
        : sp(`/select-profile`);
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const themeColor = tenant.theme_color;

  return (
    <StorefrontLayout tenant={tenant}>
      <div className="max-w-md mx-auto px-6 py-20" data-testid="storefront-signup-page">
        <p className="text-[11px] uppercase tracking-[0.4em] text-white/40">Subscribe</p>
        <h1 className="mt-3 font-display font-bold text-4xl tracking-tighter">Join {tenant.name}</h1>
        <p className="mt-3 text-white/55 font-light text-sm">
          Already have an account?{" "}
          <Link to={sp(`/login`)} className="underline decoration-white/30 underline-offset-4 hover:decoration-white text-white" data-testid="storefront-signup-login-link">
            Sign in.
          </Link>
        </p>

        <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="storefront-signup-form">
          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-[0.3em] text-white/50">Full name</Label>
            <Input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white h-11" data-testid="storefront-signup-name" />
          </div>
          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-[0.3em] text-white/50">Email</Label>
            <Input type="email" autoComplete="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white h-11" data-testid="storefront-signup-email" />
          </div>
          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-[0.3em] text-white/50">Password</Label>
            <Input type="password" autoComplete="new-password" required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white h-11" data-testid="storefront-signup-password" />
            <div className="text-[10px] text-white/30">Min 6 characters</div>
          </div>
          {error && (
            <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 p-3" data-testid="storefront-signup-error">
              {error}
            </div>
          )}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-11 rounded-none uppercase tracking-[0.25em] text-xs"
            style={{ backgroundColor: themeColor, color: "#0a0a0a" }}
            data-testid="storefront-signup-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : `Create my ${tenant.name} account`}
          </Button>
        </form>
      </div>
    </StorefrontLayout>
  );
}
